import React, { ReactElement, ReactNode, useEffect } from 'react';
import { Form, Col, Row, InputGroup } from 'react-bootstrap';
import { useFastField } from '../../../Utils/useFastField';
import { ValueType } from 'react-select';
import ChampAutocompletion, { SelectOption } from '../ChampAutocompletion';
import ChildFormControl from './ChildFormControl';
import ChildFormControlFeedBack from './ChildFormControlFeedBack';

export interface PropsDeChamp<T> {
    value?: string;
    id: keyof T;
    titre?: ReactNode;
    children?: ReactNode;
    appendedChildren?: ReactElement;
    type?: string;
    cacherTitre?: boolean;
    textarea?: boolean;
    rows?: number;
    suggestions?: Array<string | SelectOption>;
    isCreatable?: boolean;
    disabled?: boolean;
    errorMessage?: string;
    labelCol?: number;
    placeholder?: string;
    estPetit?: boolean;
    className?: string;
    trimDateText?: boolean;
    noAutoAjust?: boolean;
    emailUnique?: boolean;
    parser?: (x: string | number) => string | number;
    notifyChange?: (newValue: any) => void;
    notifyCreate?: (newValue: any) => void;
    validateOnChangeCallback?: (value: any) => boolean; //must finish with 'callback' because there is already a prop named 'validateOnChange', but as a boolean not a function
    style?: any;
}

export function ChampFormulaire<T>(props: PropsDeChamp<T>): ReactElement {
    const {
        type,
        titre,
        id,
        children,
        appendedChildren,
        textarea,
        rows,
        cacherTitre,
        suggestions,
        disabled,
        errorMessage,
        placeholder,
        trimDateText,
        estPetit,
        noAutoAjust,
        parser,
        notifyChange,
        notifyCreate,
        validateOnChangeCallback,
        emailUnique,
        style,
    } = props;

    if (type === 'hidden') {
        return <input type="hidden" name={String(id)} value={props.value} />;
    }
    const options: SelectOption[] | undefined = suggestions?.map((s) =>
        typeof s == 'string' ? { value: s, label: s } : s,
    );
    const [field, meta, helpers] = useFastField<string | number>({ name: String(id), type, parser });
    const metaErrorStatus = meta.touched && !!meta.error;
    const errorMessages: string[] = [errorMessage, meta.error].filter((msg) => !!msg) as string[];
    let fieldValue: any = trimDateText && field.value ? (field.value as string).split('T')[0] : field.value;
    let form: ReactElement;

    if (options) {
        const onChangeHandler = (valeur: ValueType<SelectOption, false>) => {
            const newValue: any = (valeur as SelectOption)?.value ?? '';
            helpers.setValue(newValue);
            if (notifyChange) {
                notifyChange(newValue);
            }
        };

        const onCreateOptionHandler = (valeur: any) => {
            helpers.setValue(valeur);
            if (notifyCreate) notifyCreate(valeur);
        };

        let optionValue: SelectOption | null = null;

        if (fieldValue) {
            optionValue =
                options.find((o) => o.value == fieldValue) ??
                ({
                    value: fieldValue,
                    label: fieldValue,
                } as SelectOption);
        }

        form = (
            <ChampAutocompletion
                {...props}
                id={field.name}
                enErreur={metaErrorStatus}
                erreur={errorMessage ? `${errorMessage}. ${meta.error}` : meta.error}
                // props de react-select
                isClearable
                disabled={disabled}
                options={options}
                onChange={onChangeHandler}
                onCreateOption={onCreateOptionHandler}
                value={optionValue}
                style={style}
            />
        );
    } else {
        const onChangeHandler = (valeur: any) => {
            helpers.setValue(valeur);
            if (notifyChange) {
                notifyChange(valeur);
            }
        };
        const childForm: ReactElement = (
            <ChildFormControl
                id={String(id)}
                type={type}
                noAutoAjust={noAutoAjust}
                textarea={textarea}
                rows={rows}
                disabled={disabled}
                metaErrorStatus={metaErrorStatus}
                value={fieldValue}
                notifyChange={onChangeHandler}
                validateOnChangeCallback={validateOnChangeCallback}
                inputFieldProps={field}
                placeholder={placeholder}
                estPetit={estPetit}
                emailUnique={emailUnique}
                appendedChildren={appendedChildren}
            />
        );

        form = (
            <Form.Group controlId={id as string} className={props.className} style={style}>
                {!cacherTitre && <Form.Label>{titre}</Form.Label>}

                {!(id === 'courriel') ? (
                    childForm
                ) : (
                    <div style={{ height: 65 }}>
                        {childForm} <ChildFormControlFeedBack messages={errorMessages} />
                    </div>
                )}

                {children}
            </Form.Group>
        );

        if (props.labelCol) {
            form = (
                <Form.Group controlId={id as string} className={props.className} style={style}>
                    <Row>
                        <Col xs={props.labelCol}>{!cacherTitre && <Form.Label>{titre}</Form.Label>}</Col>
                        <Col>
                            {childForm}
                            <ChildFormControlFeedBack messages={errorMessages} />
                        </Col>
                    </Row>

                    {children}
                </Form.Group>
            );
        }
    }

    return form;
}

ChampFormulaire.defaultProps = {
    type: 'text',
};
